import { MapConditionApi } from 'cadenza/api-client/conditions/map-condition-api';

import './map-condition.css';

import h from 'hyperscript';
import mapIcon from '@disy/cadenza-icons/map.svg';

import { Select } from 'ui/select/select';

import { errorAlert } from 'cadenza/alert';
import { getUrlParam } from 'cadenza/utils/url-param-utils';
import { hashCode } from 'cadenza/utils/hash-code';
import { PARAMETER_CONDITION_HASH } from 'cadenza/api-client/api-constants';
import { icon } from 'cadenza/utils/icon/icon';
import { logger } from 'cadenza/utils/logging';

import { ConditionElement } from '../condition-element';

import i18n from './messages.properties';

const NAME = 'd-map-condition';
export class MapCondition extends ConditionElement {

  #select;
  #latestSetValuesPromise; // for keeping consistency for other conditions, see onConditionChange

  connectedCallback () {
    this.classList.add(NAME);
    const data = this.previousElementSibling.value;
    this._mapConditionApi = new MapConditionApi({
      conditionSetId: this.conditionSetId,
      conditionName: this.conditionName,
      conditionId: this.conditionId,
      conditionValuesSetHashGetter: () => this.conditionValuesSetHash
    });

    const elementRight = h('button.dialog-trigger.button.button-icon.button-borderless', {
      title: i18n('dialog-trigger.title'),
      onclick: event => {
        event.preventDefault();
        this._showDialog();
      }
    }, icon(mapIcon));

    /*
     * Intentionally empty.
     * removeOnly: it is not possible to search for and add map condition rows from filter panel.
     */
    this.#select = new Select({
      elementRight,
      multiple: true,
      removeOnly: true,
      dropdownParent: 'stacking-context'
    });

    this.#select.addEventListener('change', () => this.onConditionChange());

    this.append(this.#select);

    this.defaultValue = this.prepareRows(data.defaultValue || []);
    this.value = this.prepareRows(data.value || []);
    this.initChanged();
  }

  set value (rows) {
    this.#select.selection = rows;
  }

  get value () {
    return this.#select.selection;
  }

  onConditionChange () {
    // the filter controller is not availabe in workbooks
    this.filterController?.disableOtherConditionsIfNeededForConsistency([ this ]);
    const rows = this.value.map(row => row.value);
    const setValuesPromise = this._mapConditionApi.setValue(rows);
    this.#latestSetValuesPromise = setValuesPromise;
    setValuesPromise
      .then((json) => super.onConditionChange(json))
      .catch(error => this.handleConditionValueChangeError(error))
      .finally(() => {
        // if the changes are not fully processed yet, disable the other conditions again.
        const enable = setValuesPromise === this.#latestSetValuesPromise;
        // the filter controller is not availabe in workbooks
        this.filterController?.setConditionsDisabledForConsistency(enable, this);
      });
  }

  _showDialog () {
    return this._mapConditionApi.registerConditionMap().then(mapId => {
      this._mapId = mapId;
      // eslint-disable-next-line promise/no-nesting
      return import(/* webpackChunkName: "map-condition-modal" */ './map-condition-modal').then(({ MapConditionModal }) => {
        const dialog = new MapConditionModal({
          icon: icon(mapIcon),
          title: this.conditionLabel,
          mapId,
          conditionSetId: this.conditionSetId,
          conditionId: this.conditionId,
          conditionValuesSetHash: getUrlParam({ name: PARAMETER_CONDITION_HASH }),
          maxSelection: this.maxSelection
        });
        dialog.addEventListener('close', () => {
          this._mapConditionApi.unregisterConditionMap(mapId);
          dialog.remove();
          if (dialog.returnValue) {
            // eslint-disable-next-line promise/no-nesting
            this._mapConditionApi
              .getConditionSelectionFromFeatureSelection(dialog.returnValue)
              .then(conditionValue => {
                this.value = conditionValue.map((row) => Object.assign(row, {
                  id: String(hashCode(JSON.stringify(row.values))),
                  value: row.values,
                  text: row.formatted.filter(v => v != null).join(', ')
                }));
                this.onConditionChange();
              })
              .catch(error => logger.error('Could not get condition selection for map condition', error));
          }
        });
        document.body.appendChild(dialog);
        dialog.showModal();
      });
    }).catch((error) => {
      logger.error('Error while showing the map condition', error);
      errorAlert(i18n('error.dialog.title'), i18n('error.dialog.message'));
      this.conditionApi.unregisterConditionMap(this._mapId);
    });
  }

  prepareRows (data) {
    return data.map(row => {
      row.id = String(hashCode(JSON.stringify(row.values)));
      row.value = row.values;
      return row;
    });
  }

  get numberOfConditionValueRows () {
    return this.value.length;
  }

  get maxSelection () {
    return Number(this.getAttribute('max-selection'));
  }

}

customElements.define(NAME, MapCondition);
